import { useState } from 'react';
import type { Source } from '../lib/types.ts';
import { api } from '../lib/api.ts';

interface Props {
  sources: Source[];
  onUpdate?: () => void;
}

function statusDot(source: Source): JSX.Element {
  if (source.has_error_today) return <span style={{ color: '#c0392b', fontWeight: 700 }} title={source.last_error ?? ''}>✗</span>;
  if (!source.active) return <span style={{ color: '#bbb' }}>–</span>;
  if (source.last_fetched_at) return <span style={{ color: '#27ae60' }}>✓</span>;
  return <span style={{ color: '#e67e22' }}>?</span>;
}

function fmtTs(ts: number | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });
}

export function SourceTable({ sources, onUpdate }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [cronInput, setCronInput] = useState('');
  const [busy, setBusy] = useState(false);

  async function toggleActive(source: Source) {
    setBusy(true);
    try {
      await api.sources.update(source.id, { active: !source.active });
      onUpdate?.();
    } finally {
      setBusy(false);
    }
  }

  async function saveCron(sourceId: string) {
    setBusy(true);
    try {
      await api.sources.update(sourceId, { cron_schedule: cronInput });
      setEditing(null);
      onUpdate?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
          <th style={th}>Status</th>
          <th style={th}>Source</th>
          <th style={th}>Category</th>
          <th style={th}>Cadence</th>
          <th style={th}>Last fetch</th>
          <th style={th}>Items today</th>
          <th style={th}>Avg score</th>
          <th style={th}>Cron</th>
          <th style={th}>Active</th>
        </tr>
      </thead>
      <tbody>
        {sources.map(s => (
          <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={td}>{statusDot(s)}</td>
            <td style={td}>
              <strong style={{ color: '#111' }}>{s.name}</strong>
              {s.last_error && <div style={{ fontSize: 10, color: '#c0392b', marginTop: 2 }}>{s.last_error.slice(0, 80)}</div>}
            </td>
            <td style={td}><span style={{ fontSize: 11, color: '#999' }}>{s.category}</span></td>
            <td style={td}>{s.cadence}</td>
            <td style={td}>{fmtTs(s.last_fetched_at)}</td>
            <td style={td}>{s.items_today ?? 0}</td>
            <td style={td}>{s.avg_score != null ? s.avg_score.toFixed(1) : '—'}</td>
            <td style={td}>
              {editing === s.id ? (
                <span>
                  <input value={cronInput} onChange={e => setCronInput(e.target.value)}
                    style={{ width: 120, fontSize: 11, fontFamily: 'monospace' }} />
                  <button onClick={() => saveCron(s.id)} disabled={busy} style={{ marginLeft: 4, fontSize: 11 }}>Save</button>
                  <button onClick={() => setEditing(null)} style={{ marginLeft: 2, fontSize: 11 }}>×</button>
                </span>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', color: '#555' }}
                  onClick={() => { setEditing(s.id); setCronInput(s.cron_schedule ?? ''); }}>
                  {s.cron_schedule ?? '—'}
                </span>
              )}
            </td>
            <td style={td}>
              <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} disabled={busy} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600, fontSize: 11, color: '#555' };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
